import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, Clock, AlertCircle, Plus, ArrowLeft, LogOut, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Timeline = () => {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [lembretes, setLembretes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [user, setUser] = useState(null);
  const [tiposServico, setTiposServico] = useState([]);
  const [editingLembrete, setEditingLembrete] = useState(null);
  const [formData, setFormData] = useState({
    cliente_nome: '',
    tipo_servico: '',
    data_renovacao: ''
  });

  useEffect(() => {
    const userData = localStorage.getItem('lembrou_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    // Buscar tipos de serviço
    fetchTiposServico();
    
    if (qrId) {
      fetchQRData();
    } else {
      const token = localStorage.getItem('lembrou_token');
      if (token) {
        fetchMyLembretes();
      } else {
        setLoading(false);
      }
    }
  }, [qrId]);

  const fetchTiposServico = async () => {
    try {
      const response = await axios.get(`${API}/servicos`);
      setTiposServico(response.data);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
      // Fallback com serviços básicos
      setTiposServico([
        { id: 1, nome: 'Seguro Auto' },
        { id: 2, nome: 'Seguro Vida' },
        { id: 3, nome: 'Consulta' },
        { id: 4, nome: 'Revisão' },
        { id: 5, nome: 'Outro' }
      ]);
    }
  };

  const fetchQRData = async () => {
    try {
      const response = await axios.get(`${API}/qr/${qrId}`);
      setLembretes([response.data]);
      toast.success('Lembrete carregado!');
    } catch (error) {
      toast.error('QR Code não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLembretes = async () => {
    try {
      const token = localStorage.getItem('lembrou_token');
      const response = await axios.get(`${API}/lembretes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLembretes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar lembretes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLembrete = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('lembrou_token');
      
      if (editingLembrete) {
        // Editar lembrete existente
        await axios.put(`${API}/lembretes/${editingLembrete.id}`, {
          ...formData,
          data_vigencia: editingLembrete.data_vigencia || new Date().toISOString().split('T')[0],
          recorrencia: editingLembrete.recorrencia || 'anual',
          observacoes: editingLembrete.observacoes || ''
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Lembrete atualizado!');
        setEditingLembrete(null);
      } else {
        // Criar novo lembrete
        await axios.post(`${API}/lembretes`, {
          ...formData,
          data_vigencia: new Date().toISOString().split('T')[0],
          recorrencia: 'anual',
          observacoes: ''
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Lembrete adicionado!');
      }
      
      setShowAddForm(false);
      setFormData({ cliente_nome: '', tipo_servico: '', data_renovacao: '' });
      fetchMyLembretes();
    } catch (error) {
      toast.error(editingLembrete ? 'Erro ao atualizar lembrete' : 'Erro ao adicionar lembrete');
    }
  };

  const handleEditLembrete = (lembrete) => {
    setEditingLembrete(lembrete);
    setFormData({
      cliente_nome: lembrete.cliente_nome || '',
      tipo_servico: lembrete.tipo_servico || '',
      data_renovacao: lembrete.data_renovacao || ''
    });
    setShowAddForm(true);
  };

  const handleDeleteLembrete = async (lembreteId) => {
    if (!window.confirm('Tem certeza que deseja excluir este lembrete?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('lembrou_token');
      await axios.delete(`${API}/lembretes/${lembreteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lembrete excluído!');
      fetchMyLembretes();
    } catch (error) {
      toast.error('Erro ao excluir lembrete');
    }
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingLembrete(null);
    setFormData({ cliente_nome: '', tipo_servico: '', data_renovacao: '' });
  };

  const handleLogout = () => {
    localStorage.removeItem('lembrou_token');
    localStorage.removeItem('lembrou_user');
    navigate('/');
  };

  const calculateDaysRemaining = (dateString) => {
    const renewalDate = new Date(dateString);
    const today = new Date();
    const diffTime = renewalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getCardColor = (daysRemaining) => {
    if (daysRemaining < 0) return 'border-red-300 bg-red-50';
    if (daysRemaining <= 7) return 'border-red-300 bg-red-50';
    if (daysRemaining <= 30) return 'border-red-300 bg-red-50';
    return 'border-green-300 bg-green-50';
  };

  const getStatusText = (daysRemaining) => {
    if (daysRemaining < 0) return 'Vencido';
    if (daysRemaining === 0) return 'Vence hoje!';
    if (daysRemaining === 1) return 'Vence amanhã!';
    return `${daysRemaining} dias restantes`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg text-gray-600">Carregando...</div>
      </div>
    );
  }

  const isAuthenticated = !!localStorage.getItem('lembrou_token');

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              data-testid="back-to-home-button"
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            {isAuthenticated && (
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="rounded-xl"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            )}
          </div>
          <div className="text-center">
            <img src="/icon-lembrou.jpg" alt="Lembrou?" className="w-16 h-16 mx-auto mb-3 rounded-2xl" />
            <h1 className="text-2xl font-bold text-gray-900">Minha Timeline</h1>
            <p className="text-sm text-gray-500 mt-1">
              {user ? `Olá, ${user.nome_empresa}` : 'Seus lembretes organizados'}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Login prompt if not authenticated */}
        {!isAuthenticated && !qrId && (
          <Card className="p-6 rounded-2xl border-2 border-red-200 bg-red-50 mb-6">
            <div className="text-center">
              <p className="text-red-900 font-medium mb-3">
                Faça login para ver seus lembretes
              </p>
              <Button
                data-testid="login-timeline-button"
                onClick={() => navigate('/auth')}
                className="rounded-2xl bg-primary hover:bg-primary-hover"
              >
                Fazer Login
              </Button>
            </div>
          </Card>
        )}

        {/* Add Lembrete Button */}
        {isAuthenticated && !showAddForm && (
          <Button
            data-testid="add-lembrete-timeline-button"
            onClick={() => setShowAddForm(true)}
            className="w-full mb-6 rounded-2xl bg-primary hover:bg-primary-hover shadow-md"
          >
            <Plus className="w-5 h-5 mr-2" />
            Adicionar Novo Lembrete
          </Button>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="p-6 rounded-2xl border shadow-sm mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingLembrete ? 'Editar Lembrete' : 'Novo Lembrete'}
            </h3>
            <form onSubmit={handleAddLembrete} className="space-y-4">
              <div>
                <Label htmlFor="cliente_nome">Título</Label>
                <Input
                  id="cliente_nome"
                  data-testid="timeline-titulo-input"
                  placeholder="Ex: Renovação Seguro Auto"
                  value={formData.cliente_nome}
                  onChange={(e) => setFormData({...formData, cliente_nome: e.target.value})}
                  required
                  className="rounded-xl h-11"
                />
              </div>
              <div>
                <Label htmlFor="tipo_servico">Tipo</Label>
                <select
                  id="tipo_servico"
                  data-testid="timeline-tipo-input"
                  value={formData.tipo_servico}
                  onChange={(e) => setFormData({...formData, tipo_servico: e.target.value})}
                  required
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-red-100 outline-none h-11"
                >
                  <option value="">Selecione o tipo...</option>
                  {tiposServico.map((tipo) => (
                    <option key={tipo.id} value={tipo.nome}>
                      {tipo.nome}
                    </option>
                  ))}
                  <option value="Outro">Outro</option>
                </select>
              </div>
              <div>
                <Label htmlFor="data_renovacao">Data de Vencimento</Label>
                <Input
                  id="data_renovacao"
                  data-testid="timeline-data-input"
                  type="date"
                  value={formData.data_renovacao}
                  onChange={(e) => setFormData({...formData, data_renovacao: e.target.value})}
                  required
                  className="rounded-xl h-11"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  data-testid="timeline-submit-button"
                  className="flex-1 rounded-2xl bg-primary hover:bg-primary-hover"
                >
                  {editingLembrete ? 'Salvar' : 'Adicionar'}
                </Button>
                <Button
                  type="button"
                  data-testid="timeline-cancel-button"
                  onClick={handleCancelForm}
                  variant="outline"
                  className="rounded-2xl"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Lembretes List */}
        {lembretes.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum lembrete ainda
            </h2>
            <p className="text-gray-500">
              {isAuthenticated 
                ? 'Adicione seu primeiro lembrete acima'
                : 'Escaneie um QR Code para adicionar lembretes à sua timeline'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {lembretes.map((lembrete, index) => {
              const daysRemaining = calculateDaysRemaining(lembrete.data_renovacao);
              const colorClass = getCardColor(daysRemaining);

              return (
                <Card
                  key={index}
                  data-testid={`timeline-card-${index}`}
                  className={`p-6 rounded-2xl border-l-4 shadow-md ${colorClass} transition-all hover:shadow-lg`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {lembrete.cliente_nome || lembrete.tipo_servico}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {lembrete.tipo_servico}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200">
                      <Clock className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">
                        {getStatusText(daysRemaining)}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-700">
                      <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                      <span>
                        Renovação: {new Date(lembrete.data_renovacao).toLocaleDateString('pt-BR')}
                      </span>
                    </div>

                    {lembrete.observacoes && (
                      <div className="flex items-start text-sm text-gray-700 mt-3 pt-3 border-t border-gray-200">
                        <AlertCircle className="w-4 h-4 mr-2 text-gray-500 mt-0.5" />
                        <span>{lembrete.observacoes}</span>
                      </div>
                    )}
                  </div>

                  {/* Countdown Visual */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-gray-900">
                        {Math.abs(daysRemaining)}
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wide">
                        {daysRemaining < 0 ? 'dias atrasado' : 'dias restantes'}
                      </div>
                    </div>
                  </div>

                  {/* Botões de Ação - só para usuários autenticados e lembretes próprios */}
                  {isAuthenticated && (
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end gap-2">
                      <button
                        onClick={() => handleEditLembrete(lembrete)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteLembrete(lembrete.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Excluir
                      </button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer Info */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Powered by Lembrou? © 2026</p>
        </div>
      </main>
    </div>
  );
};

export default Timeline;